import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  getBasicDetails,
  getSuggestedPackage,
  getFacilityPackages,
  getAssignedPackages,
  acceptSuggestedPackage,
  assignOpPackage,
} from "../../../services/packages.services";
import {
  Row,
  Col,
  Card,
  Select,
  Button,
  Spin,
  Input,
  DatePicker,
  TimePicker,
  Table,
  Typography,
  message,
  Form,
  Space,
  Tag,
} from "antd";
import dayjs from "dayjs";
import OpBasicDetails from "./OpBasicDetails";
import AssignedPackages from "./AssignedPackages";
import inputStyles from "../../../styles/Custom/Input.module.css";
import btnStyles from "../../../styles/Custom/Button.module.css";
import styles from "./PackagesOp.module.css";
import { UserContext } from "../../../context/UserContext";

const { Option } = Select;

const purpleStyle = {
  background: "#7b61ff",
  borderColor: "#7b61ff",
  color: "#fff",
};
const priceTagList = [
  { name: "₹", id: false },
  { name: "%", id: true },
];

const PACKAGES_TABS = [
  { value: "package", label: "Package" },
  { value: "patient", label: "Patient Info" },
];

const PackagesScreen = ({ admissionId }) => {
  const { userDetails } = useContext(UserContext);
  const [form] = Form.useForm();
  const orgPrimaryColor =
    userDetails?.activeOrg?.color || "var(--ant-primary-color, #6941C6)";
  const [basicDetails, setBasicDetails] = useState(null);
  const [assignedPackages, setAssignedPackages] = useState([]);
  const [suggestedPackages, setSuggestedPackages] = useState([]);
  const [facilityPackages, setFacilityPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discount, setDiscount] = useState("");
  const [date, setDate] = useState(() => dayjs());
  const [changeMode, setChangeMode] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [discountType, setDiscountType] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [viewDetails, setViewDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("package");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolveStartMoment = (values) => {
    if (isMobile) {
      const d = values?.mobile_pkg_date;
      const t = values?.mobile_pkg_time;
      if (!d || !t) return null;
      return dayjs(d)
        .hour(t.hour())
        .minute(t.minute())
        .second(0)
        .millisecond(0);
    }
    const fromForm = values?.start_date;
    if (fromForm) return dayjs(fromForm);
    return date ? dayjs(date) : null;
  };

  const fetchAll = useCallback(async () => {
    try {
      setFetching(true);
      setLoading(true);

      if (!admissionId) {
        console.warn("Admission ID is missing. Cannot fetch package details.");
        setLoading(false);
        return;
      }

      const basic = await getBasicDetails(admissionId);
      const details = basic?.data || null;
      setBasicDetails(details);

      const facilityId = details?.facility || null;

      const assigned = await getAssignedPackages(admissionId);
      let assignedPkgs = [];
      if (assigned.data?.data) {
        const data = Array.isArray(assigned.data.data)
          ? assigned.data.data
          : [assigned.data.data];
        assignedPkgs = data.filter(
          (p) => (p.status || "").toLowerCase() === "ordered"
        );
      }
      setAssignedPackages(assignedPkgs);

      let suggestedPkgs = [];
      try {
        const suggested = await getSuggestedPackage(admissionId);
        if (suggested.data?.data) {
          suggestedPkgs = Array.isArray(suggested.data.data)
            ? suggested.data.data
            : [suggested.data.data];
        }
      } catch (err) {
        console.warn("No suggested packages found", err);
      }
      const suggestedFiltered = suggestedPkgs.filter(
        (p) => (p.status || "").toLowerCase() === "suggested"
      );
      setSuggestedPackages(suggestedFiltered);

      let facilityPkgs = [];
      if (facilityId) {
        try {
          const facility = await getFacilityPackages(facilityId);
          facilityPkgs = Array.isArray(facility.data?.data)
            ? facility.data.data
            : [];
        } catch (err) {
          console.warn("No facility packages found", err);
        }
      }
      setFacilityPackages(facilityPkgs);

      if (suggestedFiltered.length > 0) {
        const autoSelected = suggestedFiltered[0];
        setSelectedPackage({ ...autoSelected, _isSuggestion: true });
        setTotalPrice(
          Number(
            autoSelected.package?.total_price ??
              autoSelected.total_price ??
              autoSelected.price ??
              0
          )
        ); // ← add this
        setChangeMode(false);
      } else {
        setSelectedPackage(null);
        setTotalPrice(0); // ← reset on no suggestion
        setChangeMode(true);
      }
    } catch (error) {
      console.error("Error fetching package data", error);
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [admissionId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handlePackageChange = (value) => {
    setViewDetails(false);
    if (!value) {
      setSelectedPackage(null);
      setTotalPrice(0);
      return;
    }

    const [type, idStr] = String(value).split("-");
    const id = Number(idStr);

    if (type === "facility") {
      const pkg = facilityPackages.find((p) => p.id === id);
      setSelectedPackage(pkg ? { ...pkg, _isSuggestion: false } : null);
      setTotalPrice(pkg ? Number(pkg.total_price ?? pkg.price ?? 0) : 0);
    } else if (type === "suggested") {
      const sug = suggestedPackages.find((p) => p.id === id);
      setSelectedPackage(sug ? { ...sug, _isSuggestion: true } : null);
      setTotalPrice(
        sug
          ? Number(
              sug.package?.total_price ?? sug.total_price ?? sug.price ?? 0
            )
          : 0
      );
    }
  };

  const getPackagePrice = (pkg) => {
    if (!pkg) return 0;

    if (pkg.package?.total_price !== undefined)
      return Number(pkg.package.total_price);

    if (pkg.total_price !== undefined) return Number(pkg.total_price);

    if (pkg.price !== undefined) return Number(pkg.price);

    return 0;
  };

  const handleManualAssign = async (values) => {
    let discountInRupees = Number(discount);

    if (discountType === true) {
      discountInRupees = Number((discount / 100) * totalPrice).toFixed(2);
    }
    if (!selectedPackage) {
      message.warning("Please select a package to assign.");
      return;
    }

    const startMoment = resolveStartMoment(values);
    if (!startMoment) {
      message.error("Please select date and time");
      return;
    }

    try {
      setFetching(true);
      const payload = {
        op_admission: admissionId,
        package: selectedPackage.package?.id || selectedPackage.id,
        discount: discountInRupees,
        start_date: startMoment.toISOString(),
        price: getPackagePrice(selectedPackage),
      };

      await assignOpPackage(payload);

      message.success("Package assigned successfully.");
      await fetchAll();

      setSelectedPackage(null);
      form.resetFields();
      form.setFieldValue("discount", "");
      setDiscount("");
      const now = dayjs();
      setDate(now);
      form.setFieldsValue({
        start_date: now,
        mobile_pkg_date: now,
        mobile_pkg_time: now,
      });
      setChangeMode(true);
    } catch (error) {
      console.error("Error assigning package:", error);
      message.error("Failed to assign package.");
    } finally {
      setFetching(false);
    }
  };

  const handleAcceptSuggested = async () => {
    let discountInRupees = Number(discount);

    if (discountType === true) {
      discountInRupees = Number((discount / 100) * totalPrice).toFixed(2);
    }
    if (!selectedPackage || !selectedPackage._isSuggestion) {
      message.warn("No suggested package selected to accept.");
      return;
    }

    const startMoment = resolveStartMoment(form.getFieldsValue());
    if (!startMoment) {
      message.error("Please select date and time");
      return;
    }

    try {
      setFetching(true);
      const payload = {
        op_admission: admissionId,
        package: selectedPackage.package?.id || selectedPackage.id,
        discount: discountInRupees,
        start_date: startMoment.toISOString(),
        price: getPackagePrice(selectedPackage),
      };

      await acceptSuggestedPackage(selectedPackage.id, payload);

      message.success("Suggested package accepted successfully.");
      await fetchAll();
      setDiscount("");
      const now = dayjs();
      setDate(now);
      form.setFieldsValue({
        start_date: now,
        mobile_pkg_date: now,
        mobile_pkg_time: now,
      });
      setChangeMode(true);
    } catch (err) {
      console.error(err);
      message.error("Failed to accept suggested package.");
    } finally {
      setFetching(false);
    }
  };

  const getServicesData = () => {
    if (!selectedPackage) return [];
    const services = selectedPackage.package_services;
    if (Array.isArray(services)) {
      return services.map((s, idx) => {
        const name = s.service?.name || s.name || `Service ${idx + 1}`;
        const unit_price = Number(s.price ?? s.unit_price ?? 0);
        const flat_discount = Number(s.discount ?? 0);
        const qty = s.quantity ?? 1;

        const final_price = unit_price * qty - flat_discount;

        return {
          key: idx,
          name,
          unit_price,
          flat_discount,
          final_price,
          qty: s.quantity ?? 1,
        };
      });
    }

    if (
      selectedPackage.price_snapshot &&
      typeof selectedPackage.price_snapshot === "object"
    ) {
      return Object.entries(selectedPackage.price_snapshot).map(
        ([k, v], idx) => {
          const name = k;
          const unit_price = v.price ?? 0;
          const flat_discount = v.discount ?? 0;
          const final_price = unit_price - (flat_discount || 0);
          return {
            key: idx,
            name,
            unit_price,
            flat_discount,
            final_price,
            qty: v.quantity ?? 1,
          };
        }
      );
    }

    return [];
  };
  const columns = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <div style={{ color: "#888", fontSize: 12 }}>Qty: {record.qty}</div>
        </div>
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (v) => `Rs ${Number(v || 0).toFixed(2)}`,
    },
    {
      title: "Flat Discount",
      dataIndex: "flat_discount",
      key: "flat_discount",
      render: (v) => `Rs ${Number(v || 0).toFixed(2)}`,
    },
    {
      title: "Final Price",
      dataIndex: "final_price",
      key: "final_price",
      render: (v) => `Rs ${Number(v || 0).toFixed(2)}`,
    },
  ];

  const onDiscountTypeChange = (value) => {
    setDiscountType(value);
    setDiscount("");
    form.setFieldValue("discount", "");
    form.setFields([{ name: "discount", errors: [] }]);
  };
  if (loading) return <Spin tip="Loading Packages..." />;

  const servicesData = getServicesData();
  const suggestionPresent =
    suggestedPackages.length > 0 &&
    selectedPackage &&
    selectedPackage._isSuggestion;

  const filteredFacilityPackages = facilityPackages?.filter((fp) => {
    return !suggestedPackages.some((sp) => {
      const suggestedId = sp.package?.id || sp.id;
      return suggestedId === fp.id;
    });
  });
  return (
    <div className={styles.packagesContent}>
      {isMobile && (
        <div
          className={styles.packagesMobileToggle}
          role="tablist"
          aria-label="Packages view"
        >
          {PACKAGES_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.value}
              className={`${styles.packagesSegmentBtn} ${
                activeTab === tab.value ? styles.packagesSegmentBtnActive : ""
              }`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <Row gutter={[12, 12]} className={styles.packagesRow}>
        <Col
          xs={24}
          sm={24}
          md={12}
          lg={12}
          xl={12}
          className={
            isMobile && activeTab !== "patient" ? styles.packagesPanelHidden : ""
          }
        >
          <OpBasicDetails basicDetails={basicDetails} />
        </Col>
        <Col
          xs={24}
          sm={24}
          md={12}
          lg={12}
          xl={12}
          className={
            isMobile && activeTab !== "package" ? styles.packagesPanelHidden : ""
          }
        >
          <Card
            title="Assign Package"
            className={`care-item-card ${styles.assignPackageCard}`}
            style={{ "--org-primary": orgPrimaryColor }}
          >
          <Form
            layout="vertical"
            onFinish={handleManualAssign}
            form={form}
            className={styles.assignPackageForm}
            style={{ "--org-primary": orgPrimaryColor }}
            initialValues={{
              start_date: dayjs(),
              mobile_pkg_date: dayjs(),
              mobile_pkg_time: dayjs(),
            }}
          >
            <Row gutter={[12, 8]} className={styles.packageNameRow}>
              <Col xs={24} sm={12} className={styles.packageNameCol}>
                <Form.Item
                  label="Package Name"
                  name="package"
                  className={styles.assignPackagePackageField}
                  rules={[{ required: true, message: "Please select a package" }]}
                >
                  <Select
                    className={inputStyles.mdSelect}
                    placeholder="Select a Package"
                    value={
                      selectedPackage
                        ? selectedPackage._isSuggestion
                          ? `suggested-${selectedPackage.id}`
                          : `facility-${selectedPackage.id}`
                        : undefined
                    }
                    onChange={handlePackageChange}
                    disabled={
                      !changeMode && selectedPackage?._isSuggestion === true
                    }
                    showSearch
                    optionFilterProp="children"
                  >
                    {filteredFacilityPackages.length > 0 ? (
                      filteredFacilityPackages.map((pkg) => (
                        <Option
                          key={`facility-${pkg.id}`}
                          value={`facility-${pkg.id}`}
                        >
                          {pkg.name}
                          <Tag>{pkg.total_price ?? pkg.price}</Tag>
                        </Option>
                      ))
                    ) : (
                      <Option value="" disabled>
                        No facility packages available
                      </Option>
                    )}

                    {suggestedPackages.length > 0 &&
                      suggestedPackages.map((pkg) => (
                        <Option
                          key={`suggested-${pkg.id}`}
                          value={`suggested-${pkg.id}`}
                        >
                          {pkg.package?.name || pkg.package_name} (Suggested)
                          <Tag>
                            {pkg.package?.total_price ??
                              pkg.total_price ??
                              pkg.price}
                          </Tag>
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
                {suggestionPresent && !changeMode && (
                  <Space style={{ marginBottom: 8 }}>
                    <Button
                      type="primary"
                      style={purpleStyle}
                      onClick={handleAcceptSuggested}
                      loading={fetching}
                    >
                      Accept
                    </Button>

                    <Button onClick={() => setChangeMode(true)}>Change</Button>
                  </Space>
                )}
                {selectedPackage && servicesData.length > 0 && (
                  <>
                    {!viewDetails && (
                      <Typography.Link onClick={() => setViewDetails(true)}>
                        View More Details
                      </Typography.Link>
                    )}

                    {viewDetails && (
                      <>
                        <Table
                          style={{ marginBottom: "10px", marginTop: 8 }}
                          bordered
                          columns={columns}
                          dataSource={servicesData}
                          pagination={false}
                          size="small"
                          scroll={{ x: true }}
                        />
                        <Typography.Link onClick={() => setViewDetails(false)}>
                          View Less Details
                        </Typography.Link>
                      </>
                    )}
                    <br />
                  </>
                )}
              </Col>
            </Row>
            <Row gutter={[12, 8]} align="top" className={styles.discountDateRow}>
                  <Col xs={24} sm={12} className={styles.discountDateDiscountCol}>
                    <Form.Item
                      name="discount"
                      label="Discount"
                      className={styles.discountCompact}
                      rules={[
                        {
                          validator: (_, value, callback) => {
                            if (value && value !== "") {
                              if (/^[0-9]*\.?[0-9]{1,3}$/.test(value)) {
                                if (
                                  discountType === true &&
                                  Number(value) > 100
                                ) {
                                  callback(
                                    "Discount cannot be greater than 100%"
                                  );
                                } else if (
                                  discountType === false &&
                                  Number(value) > Number(totalPrice)
                                ) {
                                  callback(
                                    "Discount cannot be greater than total amount"
                                  );
                                } else {
                                  callback();
                                }
                              } else {
                                callback("Please enter a valid discount");
                              }
                            } else {
                              callback();
                            }
                          },
                        },
                      ]}
                    >
                      <Space.Compact block style={{ width: "100%" }}>
                        <Input
                          placeholder="Enter discount"
                          size={isMobile ? "large" : undefined}
                          className={inputStyles.mdInput}
                          style={{ flex: 1, minWidth: 0 }}
                          value={discount}
                          onChange={(e) => setDiscount(e.target.value)}
                        />
                        <Select
                          className={inputStyles.mdSelect}
                          size={isMobile ? "large" : undefined}
                          defaultValue={false}
                          onChange={onDiscountTypeChange}
                          popupMatchSelectWidth={false}
                        >
                          {priceTagList.map((data) => (
                            <Select.Option key={data.id} value={data.id}>
                              {data.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} className={styles.discountDateDateCol}>
                    {isMobile ? (
                      <>
                        <Form.Item
                          label="Date"
                          name="mobile_pkg_date"
                          rules={[
                            { required: true, message: "Please select a date" },
                          ]}
                        >
                          <DatePicker
                            allowClear={false}
                            className={inputStyles.mdDateInput}
                            format="YYYY-MM-DD"
                            style={{ width: "100%" }}
                            placement="bottomLeft"
                            getPopupContainer={() => document.body}
                            popupClassName="curengo-mobile-picker"
                            popupStyle={{
                              maxWidth: "min(100vw - 24px, 360px)",
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="Time"
                          name="mobile_pkg_time"
                          rules={[
                            { required: true, message: "Please select a time" },
                          ]}
                        >
                          <TimePicker
                            allowClear={false}
                            className={inputStyles.mdDateInput}
                            format="hh:mm A"
                            use12Hours
                            needConfirm={false}
                            style={{ width: "100%" }}
                            getPopupContainer={() => document.body}
                            popupClassName="curengo-mobile-picker"
                            popupStyle={{
                              maxWidth: "min(100vw - 24px, 220px)",
                            }}
                          />
                        </Form.Item>
                      </>
                    ) : (
                      <Form.Item label="Date" name="start_date">
                        <DatePicker
                          showTime={{ format: "HH:mm A" }}
                          allowClear={false}
                          value={date}
                          onChange={setDate}
                          className={inputStyles.mdDateInput}
                          style={{ width: "100%" }}
                          format="YYYY-MM-DD HH:mm A"
                        />
                      </Form.Item>
                    )}
                  </Col>
            </Row>
            {selectedPackage && (
              <div style={{ marginTop: "10px" }}>
                {discount > 0 && (
                  <>
                    <strong>Total Price After Discount:</strong> ₹
                    {discountType
                      ? (
                          totalPrice -
                          (discount / 100) * totalPrice
                        ).toFixed(2)
                      : (totalPrice - Number(discount)).toFixed(2)}
                  </>
                )}
              </div>
            )}
            <Form.Item
              className={styles.assignPackageSubmit}
              style={{ textAlign: "right" }}
            >
              <Button
                type="primary"
                htmlType="submit"
                className={`${btnStyles.mdBtn} ${styles.assignPackageSubmitBtn}`}
              >
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Card>

          <AssignedPackages assignedPackages={assignedPackages} />
        </Col>
      </Row>
    </div>
  );
};

export default PackagesScreen;
